import IPositionalElement from "../../../../common/elements/geom/IPositionalElement";
import TabletopElement from "./TabletopElement";
import TabletopViewport from "../TabletopViewport";

import { Point2d } from "../../../../common/geometry/Geom2d";

export default class TabletopPositionalElement
    extends TabletopElement
    implements IPositionalElement
{
    public static globalDrawElementBounds: boolean = false;

    protected _x: number | (() => number);
    protected _y: number | (() => number);

    constructor(args?: {
        x?: number | (() => number);
        y?: number | (() => number);
        sortIndex?: number;
    }) {
        super();

        this._x = args?.x ?? 0;
        this._y = args?.y ?? 0;
    }

    public get x(): number {
        if (typeof this._x === "number") {
            return this._x;
        } else {
            return this._x();
        }
    }
    public set x(val: number | (() => number)) {
        this._x = val;
        this.onMoved();
    }

    public get y(): number {
        if (typeof this._y === "number") {
            return this._y;
        } else {
            return this._y();
        }
    }

    public set y(val: number | (() => number)) {
        this._y = val;
        this.onMoved();
    }

    // Positional fields are redundant for positional elements, but may be overridden in children
    /** The x value of the leftmost edge of this element's bounds, in universe coordinates. */
    public get left(): number {
        return this.x;
    }
    /** The x value of the rightmost edge of this element's bounds, in universe coordinates. */
    public get right(): number {
        return this.x;
    }

    /** The y value of the topmost edge of this element's bounds, in universe coordinates. */
    public get top(): number {
        return this.y;
    }
    /** The y value of the bottommost edge of this element's bounds, in universe coordinates. */
    public get bottom(): number {
        return this.y;
    }

    /** The centerpoint of this element, based on its bounds, in universe coordinates. */
    public get center(): Point2d {
        return {
            x: (this.left + this.right) / 2,
            y: (this.top + this.bottom) / 2,
        };
    }

    public get topLeft(): Point2d {
        return { x: this.left, y: this.top };
    }

    public get topRight(): Point2d {
        return { x: this.right, y: this.top };
    }

    public get bottomLeft(): Point2d {
        return { x: this.left, y: this.bottom };
    }

    public get bottomRight(): Point2d {
        return { x: this.right, y: this.bottom };
    }

    public get position(): Point2d {
        return { x: this.x, y: this.y };
    }

    public set position(val: Point2d) {
        this._x = val.x;
        this._y = val.y;

        this.onMoved();
    }

    public move(movement: Point2d) {
        if (typeof this._x !== "number" || typeof this._y !== "number") {
            console.error(
                "Attempting to manually move a Tabletop Element with functionally-determined positional coordinates.",
            );
            return;
        }

        this.position = { x: this.x + movement.x, y: this.y + movement.y };

        this.onMoved();
    }

    public translate(x: number, y: number): void {
        if (typeof this._x !== "number" || typeof this._y !== "number") {
            console.error(
                "Attempting to translate a Tabletop Element with functionally-determined coordinates",
            );
        } else {
            this._x += x;
            this._y += y;

            this.onMoved();
        }
    }

    /** Called anytime the x or y value of the element changes. */
    protected onMoved(): void {}

    /** Render the tabletop element. Gets called every animation frame, so long as the element is not being culled. */
    public draw(
        ctx: CanvasRenderingContext2D,
        viewport: TabletopViewport,
        timestamp: number,
    ): void {}
}
